import {Component, inject, OnInit} from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {PageHeaderComponent} from '../../../../../../shared/components/common/page-header/page-header.component';
import {Card} from '../../../../../../shared/components/common/card/card';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {EditionButtons} from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {ActivatedRoute, Router} from '@angular/router';
import {ProspectosService} from '../prospectos.service';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Spinner} from '../../../../../../shared/components/ui/spinner/spinner';
import {FormStatusService} from '../../../../../../shared/services/form-status.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from '@angular/material/tabs';
import {Contactos} from '../../contactos/contactos';
import {PropuestasComerciales} from '../../propuestas-comerciales/propuestas-comerciales';
import {DocumentoTipo, GenericCombo} from '../../../../../../shared/services/interfaces/combos.interface';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {CombosService} from '../../../../../../shared/services/combos.service';
import {forkJoin} from 'rxjs';
import {DialogService} from '@shared/services/dialog.service';
// import {ConvertirClienteDialog} from './convertir-cliente-dialog/convertir-cliente-dialog';
import {Prospecto} from '../prospectos.interface';
import {Badge} from '../../../../../../shared/components/ui/badge/badge';
import {IconButton} from '../../../../../../shared/components/ui/icon-button/icon-button';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';

@Component({
  selector: 'app-detalle-prospecto',
  imports: [
    HasPermissionDirective,
    PageHeaderComponent,
    Card,
    LabelComponent,
    EditionButtons,
    ReactiveFormsModule,
    Spinner,
    MatTabGroup,
    MatTab,
    MatTabContent,
    MatTabLabel,
    Contactos,
    PropuestasComerciales,
    Badge,
    IconButton,
    SelectFieldComponent,
    TextareaFieldComponent,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './detalle-prospecto.html'
})
export class DetalleProspecto implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  idProspecto!: number;
  idContacto: number | null = null;
  loading = false;
  editable = true;
  editForm!: FormGroup;
  updating = false;
  selectedIndex = 0;
  canalesCaptacion: GenericCombo[] = [];
  documentosTipos: DocumentoTipo[] = [];

  tipoOptions: SelectOption[] = [{ value: 'F', label: 'Física' }, { value: 'J', label: 'Jurídica' }];
  get canalesCaptacionOptions(): SelectOption[] { return this.canalesCaptacion.map(x => ({ value: x.id, label: x.nombre })); }
  get documentosTiposOptions(): SelectOption[] { return this.documentosTipos.map(x => ({ value: x.id, label: x.name })); }
  prospecto: Prospecto | null = null;

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private prospectosService = inject(ProspectosService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.idProspecto = Number(this.route.snapshot.paramMap.get('id'));
    this.getCombos();
    this.getProspecto();
    this.makeForm();
  }

  private getCombos(): void {
    const canalesCaptacion = this.combosService.getCanalesCaptacionCombo();
    const documentosTipos = this.combosService.getDocumentoTipoCombo();

    forkJoin([canalesCaptacion, documentosTipos]).subscribe({
      next: (response) => {
        this.canalesCaptacion = response[0].data;
        this.documentosTipos = response[1].data;
        this.setDefaultDocumentoTipo();
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      param_canal_captacion_id: [{ value: '', disabled: this.editable }, Validators.required],
      fecha_solicitud: [{ value: '', disabled: this.editable }],
      nombre: [{ value: '', disabled: this.editable }],
      apellido: [{ value: '', disabled: this.editable }],
      documento: [{ value: '', disabled: this.editable }],
      per_documento_tipo_id: [{ value: '', disabled: this.editable }],
      nombre_empresa: { value: '', disabled: this.editable },
      tipo: [{ value: 'F', disabled: this.editable }, Validators.required],
      telefono: [{ value: '', disabled: this.editable }, Validators.required],
      email: [{ value: '', disabled: this.editable }, [Validators.required, Validators.email]],
      whatsapp: [{ value: '', disabled: this.editable }],
      descripcion: [{ value: '', disabled: this.editable }]
    });
    this.updateTipoValidators();
    this.setDefaultDocumentoTipo();
  }

  private updateTipoValidators(): void {
    const nombreControl = this.editForm.get('nombre');
    const apellidoControl = this.editForm.get('apellido');
    const nombreEmpresaControl = this.editForm.get('nombre_empresa');

    this.editForm.get('tipo')?.valueChanges.subscribe((value) => {
      // Si el tipo seleccionado es 'F' (Físico), los campos nombre y apellido serán requeridos
      if (value === 'F') {
        nombreControl?.setValidators(Validators.required);
        apellidoControl?.setValidators(Validators.required);

        nombreEmpresaControl?.clearValidators();
      } else {
        // Si no es 'F', va a ser 'J' (Jurídico), solo el campo nombre_empresa será requerido
        nombreControl?.clearValidators();
        apellidoControl?.clearValidators();

        nombreEmpresaControl?.setValidators(Validators.required);
      }

      nombreControl?.updateValueAndValidity();
      apellidoControl?.updateValueAndValidity();
      nombreEmpresaControl?.updateValueAndValidity();
      this.setDefaultDocumentoTipo();
    });
  }

  private setDefaultDocumentoTipo(): void {
    if (!this.editForm) {
      return;
    }

    const documentoTipoControl = this.editForm?.get('per_documento_tipo_id');
    if (!documentoTipoControl || documentoTipoControl.value || !this.documentosTipos.length) {
      return;
    }

    const tipo = this.editForm.get('tipo')?.value;
    const codigo = tipo === 'J' ? 'DOC_TIP_002' : 'DOC_TIP_001';
    const documentoTipo = this.documentosTipos.find((doc) => doc.email === codigo);
    if (documentoTipo) {
      documentoTipoControl.setValue(documentoTipo.id);
    }
  }

  getProspecto(): void {
    this.loading = true;
    this.prospectosService.show(this.idProspecto).subscribe({
      next: (response) => {
        if (response.status) {
          const data = {...response.data};
          // Convertir fecha ISO a formato YYYY-MM-DD para input type="date"
          if (data.fecha_solicitud) {
            data.fecha_solicitud = data.fecha_solicitud.split('T')[0];
          }
          this.editForm.patchValue(data);
          this.prospecto = response.data;
          this.setDefaultDocumentoTipo();
        }
      }, error: (error) => {
        this.errorService.handleError(error)
      }
    }).add(() => {
      this.loading = false;
    });
  }

  updateProspecto(): void {
    this.updating = true;
    const payload = this.preparePayload();

    this.prospectosService.update(this.idProspecto, payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Prospecto actualizado correctamente');
          this.refreshProspecto();
        }
      }, error: (error) => {
        this.errorService.handleError(error)
      }
    }).add(() => {
      this.updating = false;
    });
  }

  private preparePayload(): any {
    const formValue = {...this.editForm.value};

    if (formValue.tipo === 'F') {
      // Para personas físicas, eliminar nombre_empresa del payload
      delete formValue.nombre_empresa;
    } else {
      // Para personas jurídicas, eliminar nombre y apellido del payload
      delete formValue.nombre;
      delete formValue.apellido;
    }

    return formValue;
  }

  goBack(): void {
    this.router.navigate(['../'], {relativeTo: this.route});
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  refreshProspecto(): void {
    this.editableSwitcher();
    this.getProspecto();
  }

  goToPropuestas(id: number): void {
    this.selectedIndex = 2; // Tab de propuestas comerciales
    this.idContacto = id;
  }

  onTabChange(index: number): void {
    if (index !== 2) {
      // Limpiamos el id del contacto si es que no nos dirigimos al tab de propuestas comerciales
      this.idContacto = null;
    }
  }

  // La conversión usa el alta completa del cliente, precargada con el prospecto: ahí
  // se cargan también establecimientos y contrato sin volver a tipear lo que ya está.
  openCreateContacto(): void {
    this.router.navigate(['../../clientes/new'], {relativeTo: this.route, queryParams: {prospecto: this.idProspecto}});

    // Antes: diálogo con solo documento y tipo de persona.
    // this.dialogService.openForm(ConvertirClienteDialog, {
    //   size: 'md',
    //   data: {
    //     crm_prospecto_id: this.idProspecto,
    //     persona_tipo: this.editForm.get('tipo')?.value,
    //     documento: this.editForm.get('documento')?.value,
    //     per_documento_tipo_id: this.editForm.get('per_documento_tipo_id')?.value
    //   }
    // }).afterClosed().subscribe(result => {
    //   if (result) {
    //     this.router.navigate(['../../clientes/', result], {relativeTo: this.route});
    //   }
    // });
  }
}
